import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { api } from '../../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Skeleton } from '../../components/ui/skeleton';
import { 
  ArrowLeft, 
  CreditCard,
  CheckCircle,
  XCircle,
  Clock,
  User,
  Phone,
  Ticket,
  RefreshCw,
  QrCode
} from 'lucide-react';
import { toast } from 'sonner';

export default function AdminPayments() {
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [payments, setPayments] = useState([]);
  const [qrPayments, setQrPayments] = useState([]);

  const fetchPayments = async () => {
    try {
      const [oldPayments, qrData] = await Promise.all([
        api.admin.getPendingPayments().catch(() => []),
        api.admin.getPendingQRPayments().catch(() => [])
      ]);
      setPayments(oldPayments);
      setQrPayments(qrData);
    } catch (error) {
      console.error('Error fetching payments:', error);
      toast.error('Error al cargar pagos');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  useEffect(() => {
    fetchPayments();
    // Auto-refresh every 10 seconds
    const interval = setInterval(fetchPayments, 10000);
    return () => clearInterval(interval);
  }, []);

  const handleRefresh = () => {
    setRefreshing(true);
    fetchPayments();
  };

  const handleApproveQR = async (purchaseId, reference) => {
    if (!window.confirm(`¿Aprobar pago ${reference} y asignar tickets?`)) return;
    
    try {
      const result = await api.admin.approveQRPayment(purchaseId);
      toast.success(`¡Pago ${reference} aprobado! ${result.ticket_numbers.length} tickets asignados`);
      fetchPayments();
    } catch (error) {
      toast.error('Error al aprobar pago');
    }
  };

  const handleApprove = async (purchaseId) => {
    if (!window.confirm('¿Aprobar este pago y asignar los tickets?')) return;
    
    try {
      const result = await api.admin.approvePayment(purchaseId);
      toast.success(`¡Pago aprobado! ${result.ticket_numbers.length} tickets asignados`);
      fetchPayments();
    } catch (error) {
      toast.error('Error al aprobar pago');
    }
  };

  const handleReject = async (purchaseId) => {
    if (!window.confirm('¿Rechazar este pago?')) return;
    
    try {
      await api.admin.rejectPayment(purchaseId);
      toast.success('Pago rechazado');
      fetchPayments();
    } catch (error) {
      toast.error('Error al rechazar pago');
    }
  };

  const handleRejectQR = async (purchaseId, reference) => {
    if (!window.confirm(`¿Rechazar pago ${reference}?`)) return;
    
    try {
      await api.admin.rejectQRPayment(purchaseId);
      toast.success(`Pago ${reference} rechazado`);
      fetchPayments();
    } catch (error) {
      toast.error('Error al rechazar pago');
    }
  };

  const handleClearAll = async () => {
    if (!window.confirm('⚠️ ATENCIÓN: Esto eliminará TODOS los pagos y tickets.\n\n¿Estás seguro? Esta acción no se puede deshacer.')) return;
    if (!window.confirm('¿CONFIRMAR? Se borrarán todos los datos de pagos y tickets.')) return;
    
    try {
      const result = await api.admin.clearAllPayments();
      toast.success(`Limpieza completada: ${result.deleted_purchases} pagos y ${result.deleted_tickets} tickets eliminados`);
      fetchPayments();
    } catch (error) {
      toast.error('Error al limpiar datos');
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('es-CO', {
      style: 'currency',
      currency: 'COP',
      minimumFractionDigits: 0,
    }).format(amount);
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('es-CO', {
      day: 'numeric',
      month: 'short',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const getMethodColor = (method) => {
    if (method === 'nequi') return 'bg-emerald-100 text-emerald-700 border-emerald-200';
    if (method === 'bancolombia') return 'bg-yellow-100 text-yellow-700 border-yellow-200';
    return 'bg-slate-100 text-slate-700';
  };

  const getMethodBg = (method) => {
    if (method === 'nequi') return 'from-emerald-500 to-emerald-700';
    if (method === 'bancolombia') return 'from-yellow-500 to-yellow-600';
    return 'from-slate-500 to-slate-700';
  };

  const getMethodName = (method) => {
    if (method === 'nequi') return 'Bre-B';
    if (method === 'bancolombia') return 'Bancolombia';
    return method;
  };

  if (loading) {
    return (
      <div className="page-container pt-6 space-y-4">
        <Skeleton className="h-10 w-48" />
        <Skeleton className="h-40 w-full rounded-2xl" />
        <Skeleton className="h-40 w-full rounded-2xl" />
      </div>
    );
  }

  const allPayments = [...qrPayments, ...payments];
  const hasPayments = allPayments.length > 0;

  return (
    <div className="page-container pt-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Link to="/admin">
            <Button variant="ghost" size="icon" className="rounded-full">
              <ArrowLeft className="w-5 h-5" />
            </Button>
          </Link>
          <h1 className="heading-2">Pagos Pendientes</h1>
        </div>
        <div className="flex items-center gap-2">
          <Button 
            variant="destructive" 
            size="sm"
            onClick={handleClearAll}
            className="text-xs"
            data-testid="clear-all-btn"
          >
            Limpiar Todo
          </Button>
          <Button 
            variant="outline" 
            size="icon" 
            onClick={handleRefresh}
            disabled={refreshing}
            className="rounded-full"
          >
            <RefreshCw className={`w-4 h-4 ${refreshing ? 'animate-spin' : ''}`} />
          </Button>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 gap-4">
        <Card className="card-base bg-gradient-to-br from-emerald-500 to-emerald-700 text-white">
          <CardContent className="p-4 text-center">
            <p className="text-emerald-100 text-sm">Bre-B</p>
            <p className="text-2xl font-bold">
              {qrPayments.filter(p => p.payment_method === 'nequi').length}
            </p>
          </CardContent>
        </Card>
        <Card className="card-base bg-gradient-to-br from-yellow-500 to-yellow-600 text-white">
          <CardContent className="p-4 text-center">
            <p className="text-yellow-100 text-sm">Bancolombia</p>
            <p className="text-2xl font-bold">
              {qrPayments.filter(p => p.payment_method === 'bancolombia').length}
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Payments List */}
      {!hasPayments ? (
        <Card className="card-base text-center py-12">
          <CreditCard className="w-16 h-16 mx-auto mb-4 text-muted-foreground" />
          <h2 className="heading-4 mb-2">No hay pagos pendientes</h2>
          <p className="text-muted-foreground">
            Los pagos por aprobar aparecerán aquí automáticamente
          </p>
          <p className="text-xs text-muted-foreground mt-2">
            Auto-actualiza cada 10 segundos
          </p>
        </Card>
      ) : (
        <div className="space-y-4">
          {/* QR Payments */}
          {qrPayments.map((payment) => (
            <Card key={payment.id} className="card-base overflow-hidden" data-testid={`qr-payment-${payment.id}`}>
              {/* Color header strip */}
              <div className={`h-2 bg-gradient-to-r ${getMethodBg(payment.payment_method)}`} />
              
              <CardHeader className="pb-3">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className={`w-10 h-10 rounded-xl flex items-center justify-center bg-gradient-to-br ${getMethodBg(payment.payment_method)}`}>
                      <QrCode className="w-5 h-5 text-white" />
                    </div>
                    <div>
                      <CardTitle className="text-base">{payment.buyer_name}</CardTitle>
                      <p className="text-xs text-muted-foreground">{payment.buyer_phone}</p>
                    </div>
                  </div>
                  <span className={`px-3 py-1 rounded-full text-xs font-medium border ${getMethodColor(payment.payment_method)}`}>
                    {getMethodName(payment.payment_method)}
                  </span>
                </div>
              </CardHeader>
              
              <CardContent className="space-y-4">
                {/* Key Info */}
                <div className="grid grid-cols-3 gap-3">
                  <div className="bg-slate-50 rounded-xl p-3 text-center">
                    <p className="text-xs text-muted-foreground">Referencia</p>
                    <p className="font-mono font-bold text-primary">{payment.reference}</p>
                  </div>
                  <div className="bg-emerald-50 rounded-xl p-3 text-center">
                    <p className="text-xs text-emerald-600">Monto Exacto</p>
                    <p className="font-bold text-emerald-700">{formatCurrency(payment.exact_amount)}</p>
                  </div>
                  <div className="bg-blue-50 rounded-xl p-3 text-center">
                    <p className="text-xs text-blue-600">Tickets</p>
                    <p className="font-bold text-blue-700">{payment.total_tickets}</p>
                  </div>
                </div>

                {/* Time info */}
                <div className="flex items-center justify-between text-sm text-muted-foreground">
                  <div className="flex items-center gap-2">
                    <Clock className="w-4 h-4" />
                    <span>Creado: {formatDate(payment.created_at)}</span>
                  </div>
                  {payment.payment_confirmed_at && (
                    <span className="text-amber-600">
                      Confirmado: {formatDate(payment.payment_confirmed_at)}
                    </span>
                  )}
                </div>

                {/* Status Badge */}
                <div className="flex items-center gap-2">
                  {payment.status === 'pending_approval' ? (
                    <span className="px-3 py-1 rounded-full text-xs font-medium bg-amber-100 text-amber-700">
                      Usuario confirmó pago - Verificar
                    </span>
                  ) : (
                    <span className="px-3 py-1 rounded-full text-xs font-medium bg-slate-100 text-slate-600">
                      Esperando confirmación del usuario
                    </span>
                  )}
                </div>

                {/* Actions */}
                <div className="flex gap-3 pt-2">
                  <Button
                    onClick={() => handleApproveQR(payment.id, payment.reference)}
                    className="flex-1 bg-emerald-600 hover:bg-emerald-700 rounded-full"
                    data-testid={`approve-qr-${payment.id}`}
                  >
                    <CheckCircle className="w-4 h-4 mr-2" />
                    Aprobar
                  </Button>
                  <Button
                    onClick={() => handleReject(payment.id)}
                    variant="outline"
                    className="flex-1 rounded-full text-destructive border-destructive/20"
                    data-testid={`reject-qr-${payment.id}`}
                  >
                    <XCircle className="w-4 h-4 mr-2" />
                    Rechazar
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))}

          {/* Legacy Payments (Nequi manual) */}
          {payments.map((payment) => (
            <Card key={payment.id} className="card-base" data-testid={`payment-card-${payment.id}`}>
              <CardHeader className="pb-3">
                <div className="flex items-center justify-between">
                  <CardTitle className="text-lg flex items-center gap-2">
                    <User className="w-5 h-5" />
                    {payment.user_name || 'Usuario'}
                  </CardTitle>
                  <span className="px-2 py-1 rounded-full text-xs font-medium bg-amber-100 text-amber-700">
                    Pendiente (Legacy)
                  </span>
                </div>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <p className="text-muted-foreground">Monto</p>
                    <p className="font-bold text-primary text-lg">{formatCurrency(payment.amount_paid)}</p>
                  </div>
                  <div>
                    <p className="text-muted-foreground">Tickets</p>
                    <p className="font-bold">{payment.total_tickets}</p>
                  </div>
                </div>

                <div className="space-y-2 text-sm">
                  <div className="flex items-center gap-2">
                    <Phone className="w-4 h-4 text-muted-foreground" />
                    <span>{payment.user_phone || 'N/A'}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Ticket className="w-4 h-4 text-muted-foreground" />
                    <span>Ref: {payment.payment_reference || payment.id.slice(0, 8).toUpperCase()}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Clock className="w-4 h-4 text-muted-foreground" />
                    <span>{formatDate(payment.created_at)}</span>
                  </div>
                </div>

                <div className="flex gap-3 pt-2">
                  <Button
                    onClick={() => handleApprove(payment.id)}
                    className="flex-1 bg-success hover:bg-success/90 rounded-full"
                    data-testid={`approve-payment-${payment.id}`}
                  >
                    <CheckCircle className="w-4 h-4 mr-2" />
                    Aprobar
                  </Button>
                  <Button
                    onClick={() => handleReject(payment.id)}
                    variant="outline"
                    className="flex-1 rounded-full text-destructive border-destructive/20"
                    data-testid={`reject-payment-${payment.id}`}
                  >
                    <XCircle className="w-4 h-4 mr-2" />
                    Rechazar
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Help text */}
      <Card className="card-base bg-blue-50 border-blue-200">
        <CardContent className="p-4">
          <h4 className="font-medium text-blue-800 mb-2">Guía de Validación</h4>
          <ul className="text-sm text-blue-700 space-y-1">
            <li>• Verifica el <strong>monto exacto</strong> en tu app Bre-B o Bancolombia</li>
            <li>• Busca la <strong>referencia</strong> en la descripción del pago</li>
            <li>• Al aprobar, los tickets se asignan automáticamente</li>
          </ul>
        </CardContent>
      </Card>
    </div>
  );
}
